import React, { useState, useEffect } from 'react';
import { format, parseISO, isEqual, formatISO, startOfToday } from 'date-fns';
import {
  Grid,
  TextField,
  Typography,
  Box,
  Switch,
  FormControlLabel,
  Alert,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

function OperationalStatusSection({ location, isEditing, onDataChange, disabled }) {
  // Store original values to detect real changes
  const originalValues = {
    isActive: !location?.closeDate,
    openDate: location?.openDate ? parseISO(location.openDate) : null,
    closeDate: location?.closeDate ? parseISO(location.closeDate) : null,
    ownershipType: location?.ownershipType || '',
    conceptId: location?.conceptId || ''
  };

  const [formData, setFormData] = useState({
    ...originalValues,
    changeReason: ''
  });

  const [statusChanging, setStatusChanging] = useState(false);

  // Reset form when location changes or when editing mode changes
  useEffect(() => {
    if (location) {
      setFormData({
        isActive: !location.closeDate,
        openDate: location.openDate ? parseISO(location.openDate) : null,
        closeDate: location.closeDate ? parseISO(location.closeDate) : null,
        ownershipType: location.ownershipType || '',
        conceptId: location.conceptId || '',
        changeReason: ''
      });
      setStatusChanging(false);
    }
  }, [location, isEditing]); // Added isEditing to reset on edit mode change

  const handleChange = (field, value) => {
    let newFormData = { ...formData };
    
    if (field === 'isActive') {
      newFormData.isActive = value;
      if (value) {
        // Reopening location
        newFormData.closeDate = null;
      } else {
        // Closing location
        newFormData.closeDate = startOfToday();
      }
      
      // Check if this is actually a change from the original state
      const isActuallyChanging = newFormData.isActive !== originalValues.isActive;
      setStatusChanging(isActuallyChanging);
      
      // Clear change reason if we're back to original state
      if (!isActuallyChanging) {
        newFormData.changeReason = '';
      }
    } else {
      newFormData[field] = value;
    }
    
    setFormData(newFormData);
    
    // Check if there are any actual changes from original
    const hasChanges = 
      newFormData.isActive !== originalValues.isActive ||
      newFormData.ownershipType !== originalValues.ownershipType ||
      newFormData.conceptId !== originalValues.conceptId ||
      !isEqual(newFormData.openDate, originalValues.openDate) ||
      !isEqual(newFormData.closeDate, originalValues.closeDate);
    
    // Notify parent of changes
    if (onDataChange) {
      // Only pass data if there are actual changes AND change reason is filled
      if (hasChanges && newFormData.changeReason?.trim()) {
        onDataChange({
          openDate: newFormData.openDate ? formatISO(newFormData.openDate) : null,
          closeDate: newFormData.closeDate ? formatISO(newFormData.closeDate) : null,
          ownershipType: newFormData.ownershipType,
          conceptId: newFormData.conceptId,
          changeReason: newFormData.changeReason
        });
      } else if (hasChanges) {
        // Has changes but no reason - pass data but it won't enable save
        onDataChange({
          openDate: newFormData.openDate ? formatISO(newFormData.openDate) : null,
          closeDate: newFormData.closeDate ? formatISO(newFormData.closeDate) : null,
          ownershipType: newFormData.ownershipType,
          conceptId: newFormData.conceptId,
          changeReason: newFormData.changeReason
        });
      } else {
        // Signal no changes by passing null
        onDataChange(null);
      }
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    try {
      const d = typeof date === 'string' ? parseISO(date) : date;
      return format(d, 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  };

  if (!isEditing) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography variant="subtitle2">Status:</Typography>
            <Chip 
              label={formData.isActive ? 'Active' : 'Inactive'} 
              color={formData.isActive ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Open Date</Typography>
          <Typography variant="body2">
            {formData.openDate ? formatDate(formData.openDate) : '-'}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Close Date</Typography>
          <Typography variant="body2">
            {formData.closeDate ? formatDate(formData.closeDate) : '-'}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Ownership Type</Typography>
          <Typography variant="body2">{formData.ownershipType || '-'}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Concept</Typography>
          <Typography variant="body2">{formData.conceptId || '-'}</Typography>
        </Grid>
      </Grid>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => handleChange('isActive', e.target.checked)}
                disabled={disabled}
              />
            }
            label={formData.isActive ? 'Location is Active' : 'Location is Inactive'}
          />
        </Grid>
        
        {statusChanging && (
          <Grid item xs={12}>
            <Alert severity="warning" variant="outlined">
              {formData.isActive 
                ? 'You are reopening this location. Please provide a reason below.'
                : 'You are closing this location. Please provide a reason below.'}
            </Alert>
          </Grid>
        )}
        
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Open Date"
            value={formData.openDate}
            onChange={(value) => handleChange('openDate', value)}
            disabled={disabled}
            renderInput={(params) => <TextField {...params} fullWidth size="small" />}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small'
              }
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <DatePicker
            label="Close Date"
            value={formData.closeDate}
            onChange={(value) => handleChange('closeDate', value)}
            disabled={disabled || formData.isActive}
            renderInput={(params) => <TextField {...params} fullWidth size="small" />}
            slotProps={{
              textField: {
                fullWidth: true,
                size: 'small',
                helperText: formData.isActive ? 'Location is active' : null
              }
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Ownership Type"
            value={formData.ownershipType}
            onChange={(e) => handleChange('ownershipType', e.target.value)}
            disabled={disabled}
            select
            SelectProps={{
              native: true
            }}
          >
            <option value="Company">Company</option>
            <option value="Franchise">Franchise</option>
            <option value="Licensed">Licensed</option>
            <option value="Joint Venture">Joint Venture</option>
          </TextField>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            size="small"
            label="Concept"
            value={formData.conceptId}
            onChange={(e) => handleChange('conceptId', e.target.value)}
            disabled={disabled}
          />
        </Grid>
        
        {/* Change reason required for any changes */}
        {isEditing && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Change Reason"
              value={formData.changeReason}
              onChange={(e) => handleChange('changeReason', e.target.value)}
              disabled={disabled}
              required
              multiline
              rows={2}
              helperText="Please provide a reason for these changes"
            />
          </Grid>
        )}
        
        {/* Validation messages */}
        {formData.closeDate && formData.openDate && formData.closeDate < formData.openDate && (
          <Grid item xs={12}>
            <Alert severity="error" variant="outlined">
              Close date cannot be before open date
            </Alert>
          </Grid>
        )}
      </Grid>
    </LocalizationProvider>
  );
}

export default OperationalStatusSection;